/**
 * One place that decides the DOMAIN for the `dupr_access_token` / `dupr_refresh_token` cookies,
 * and the only place allowed to write them.
 *
 * These cookies exist so a signed-in session is visible to sibling DUPR web properties on the same
 * environment (they are written for other subdomains and never read back by this app). The domain
 * therefore has to be a shared PARENT of those siblings -- but only the ones in the SAME
 * environment. It previously was not: every non-prod build used a flat `.dupr.gg`, and `.dupr.gg`
 * is not one environment. It is the parent of `uat.dupr.gg` (the UAT app), `api.uat.dupr.gg` (the
 * UAT API), and `dev.dupr.gg`. It was also the parent of `api.dupr.gg`, the PRODUCTION API host at
 * the time -- so a token minted in UAT was sent by the browser to the production API and shared
 * with dev, and vice versa. (Production has since moved to `api.dupr.com`; `.dupr.gg` no longer
 * reaches the prod API, but it still spans UAT and dev, so the per-environment scoping stands.)
 *
 * The domain is derived from the actual host the browser is on, not from the build-time
 * environment flag, for two reasons: a cookie domain must be a suffix of the current host or the
 * browser silently drops it (so a hardcoded `.uat.dupr.gg` would vanish on an Amplify preview
 * served from `*.amplifyapp.com`), and deriving from the host makes "can this leak to another
 * environment" answerable by reading this one function.
 */

const ACCESS_COOKIE = 'dupr_access_token'
const REFRESH_COOKIE = 'dupr_refresh_token'

/**
 * The cookie domain for the current host, or `undefined` for a host-only cookie.
 *
 * `undefined` -- no `domain=` attribute -- is the safe default: the cookie is then readable only
 * by the exact host that set it and can never reach a sibling environment. Only the two known
 * multi-subdomain families widen to a shared parent, and each parent stays inside one environment.
 */
export function authCookieDomain(hostname: string = window.location.hostname): string | undefined {
  // Production web family: dashboard.dupr.com and any other *.dupr.com property. `.dupr.com` holds
  // only production -- UAT and dev live under dupr.gg -- so this parent never crosses environments.
  if (hostname === 'dupr.com' || hostname.endsWith('.dupr.com')) return '.dupr.com'

  // UAT family: uat.dupr.gg + api.uat.dupr.gg. Deliberately NOT `.dupr.gg`, which would also cover
  // every dev subdomain (and, until it moved to api.dupr.com, the production API host api.dupr.gg).
  if (hostname === 'uat.dupr.gg' || hostname.endsWith('.uat.dupr.gg')) return '.uat.dupr.gg'

  // dev.dupr.gg, Amplify PR previews on *.amplifyapp.com, localhost: host-only. No shared parent,
  // so nothing another environment can read.
  return undefined
}

function writeCookie(name: string, value: string, domain: string | undefined): void {
  const domainAttr = domain ? `;domain=${domain}` : ''
  document.cookie = `${name}=${value}${domainAttr};path=/`
}

/**
 * Removes a cookie on a given domain.
 *
 * Writing an empty VALUE does not remove a cookie -- it leaves a present, empty cookie on that
 * domain, and the browser keeps sending it. That is how a `.dupr.gg` cookie kept reappearing in
 * UAT: the legacy-cleanup below wrote `name=;domain=.dupr.gg`, which SET an empty `.dupr.gg`
 * cookie on every login rather than deleting the old one. A past `expires` is what actually
 * deletes it; on a domain that has no such cookie it is a silent no-op (the browser will not
 * create an already-expired cookie), so this is safe to call unconditionally.
 */
function deleteCookie(name: string, domain: string | undefined): void {
  const domainAttr = domain ? `;domain=${domain}` : ''
  document.cookie = `${name}=${domainAttr};path=/;expires=Thu, 01 Jan 1970 00:00:00 GMT`
}

/**
 * Neutralizes the legacy flat `.dupr.gg` cookies that earlier builds set.
 *
 * Existing browsers that signed in before this change still hold a `.dupr.gg` cookie, and it keeps
 * being sent cross-environment until it is removed on that exact domain. Deleting it (past expiry)
 * on the next set or clear -- from any dupr.gg host, which is allowed to write its parent -- cleans
 * up the leak. Deletion, NOT an empty write: an empty write leaves a present `.dupr.gg` cookie that
 * the browser keeps sending, which is the bug this used to cause. Not done on `.dupr.com`, where
 * the shared parent was always a single environment and there is nothing to undo.
 */
function clearLegacySharedCookies(hostname: string): void {
  if (hostname === 'dupr.gg' || hostname.endsWith('.dupr.gg')) {
    deleteCookie(ACCESS_COOKIE, '.dupr.gg')
    deleteCookie(REFRESH_COOKIE, '.dupr.gg')
  }
}

/** Writes the auth cookies on the environment-scoped domain. Pass `refreshToken` to write it too. */
export function writeAuthCookies(accessToken: string, refreshToken?: string): void {
  const domain = authCookieDomain()
  writeCookie(ACCESS_COOKIE, accessToken, domain)
  if (refreshToken !== undefined) writeCookie(REFRESH_COOKIE, refreshToken, domain)
  clearLegacySharedCookies(window.location.hostname)
}

/** Deletes both auth cookies on the environment-scoped domain (and the legacy flat one). */
export function clearAuthCookies(): void {
  const domain = authCookieDomain()
  deleteCookie(ACCESS_COOKIE, domain)
  deleteCookie(REFRESH_COOKIE, domain)
  clearLegacySharedCookies(window.location.hostname)
}
